import { useEffect, type ReactNode } from 'react'
import type { ModuleEntry } from './modules.ts'

/**
 * Navegação do CELULAR e do TABLET (SPEC-20261007-0909-layout-mobile-barra-e-inicio).
 *
 * Pedido do Cristian (2026-10-07): no dispositivo móvel a aplicação tem layout
 * de app — e SÓ nele; o computador, mesmo com a janela estreita, continua com
 * o rail. A navegação sai do rail em gaveta (aberto pelo ☰) e vai para uma
 * barra de abas no pé da tela, ao alcance do polegar: Início, os módulos mais
 * usados, e "Mais" com o resto e as ações da pessoa.
 */

/**
 * Módulos que ganham aba própria, nesta ordem de preferência. Entram os três
 * primeiros que o plano do workspace libera; o resto vai para o "Mais".
 */
export const PREFERIDOS_NAS_ABAS = ['kanbans', 'channels', 'calendar', 'meetings', 'mail', 'docs'] as const
const ABAS_DE_MODULO = 3

export function modulosDasAbas(modulos: readonly ModuleEntry[]): ModuleEntry[] {
  const porId = new Map(modulos.map((m) => [m.id, m]))
  return PREFERIDOS_NAS_ABAS.map((id) => porId.get(id))
    .filter((m): m is ModuleEntry => !!m)
    .slice(0, ABAS_DE_MODULO)
}

type Rotulo = (module: ModuleEntry) => string

function Selo({ n, rotulo }: { n: number; rotulo: string }) {
  if (n <= 0) return null
  return (
    <span className="shell__aba-selo" aria-label={rotulo}>
      {n > 99 ? '99+' : n}
    </span>
  )
}

export function AbasDoCelular({
  modulos,
  atualId,
  noInicio,
  maisAberto,
  naoLidasDoChat,
  label,
  onIr,
  onMais,
}: {
  /** Os módulos que o rail mostraria (plano e ordem já aplicados). */
  modulos: readonly ModuleEntry[]
  atualId: string | null
  noInicio: boolean
  maisAberto: boolean
  naoLidasDoChat: number
  label: Rotulo
  onIr: (rota: string) => void
  onMais: () => void
}) {
  const nasAbas = modulosDasAbas(modulos)
  // o "Mais" acende quando a tela aberta é de um módulo que mora nele
  const maisAtivo = maisAberto || (!noInicio && atualId !== null && !nasAbas.some((m) => m.id === atualId))

  const aba = (chave: string, icone: string, texto: string, ativa: boolean, onClick: () => void, extra?: ReactNode) => (
    <button
      key={chave}
      type="button"
      className={`shell__aba${ativa ? ' shell__aba--on' : ''}`}
      aria-current={ativa && chave !== 'mais' ? 'page' : undefined}
      onClick={onClick}
    >
      <span className="shell__aba-icone">
        <i className={icone} aria-hidden />
        {extra}
      </span>
      <span className="shell__aba-texto">{texto}</span>
    </button>
  )

  return (
    <nav className="shell__abas" aria-label="Navegação principal">
      {aba('inicio', 'fas fa-home', 'Início', noInicio && !maisAberto, () => onIr('/home'))}
      {nasAbas.map((m) =>
        aba(
          m.id,
          m.icon,
          label(m),
          !maisAberto && atualId === m.id,
          () => onIr(m.route),
          m.id === 'channels' ? (
            <Selo
              n={naoLidasDoChat}
              rotulo={`${naoLidasDoChat} ${naoLidasDoChat === 1 ? 'mensagem não lida' : 'mensagens não lidas'}`}
            />
          ) : null,
        ),
      )}
      {aba('mais', 'fas fa-th-large', 'Mais', maisAtivo, onMais)}
    </nav>
  )
}

/**
 * Folha do "Mais": sobe do pé da tela com os módulos que não couberam nas abas
 * e, no topo, quem está usando — o toque ali abre o menu de perfil (conta,
 * preferências, status, sair), o mesmo do avatar do rail.
 */
export function FolhaMais({
  aberta,
  modulos,
  atualId,
  label,
  usuario,
  avatar,
  naoLidasDoChat,
  onIr,
  onFechar,
  onPerfil,
  onMembros,
}: {
  aberta: boolean
  modulos: readonly ModuleEntry[]
  atualId: string | null
  label: Rotulo
  usuario: { name: string; email: string }
  avatar: ReactNode
  naoLidasDoChat: number
  onIr: (rota: string) => void
  onFechar: () => void
  onPerfil: () => void
  onMembros: () => void
}) {
  // Esc fecha, como toda sobreposição do app
  useEffect(() => {
    if (!aberta) return
    const tecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onFechar()
    }
    window.addEventListener('keydown', tecla)
    return () => window.removeEventListener('keydown', tecla)
  }, [aberta, onFechar])

  if (!aberta) return null
  const nasAbas = new Set(modulosDasAbas(modulos).map((m) => m.id))
  const resto = modulos.filter((m) => !nasAbas.has(m.id))

  return (
    <>
      <div className="shell__folha-scrim" aria-hidden onClick={onFechar} />
      <div className="shell__folha" role="dialog" aria-modal="true" aria-label="Mais opções">
        <span className="shell__folha-alca" aria-hidden />
        <button type="button" className="shell__folha-pessoa" onClick={onPerfil}>
          <span className="shell__folha-avatar">{avatar}</span>
          <span className="shell__folha-quem">
            <strong>{usuario.name}</strong>
            <span>{usuario.email}</span>
          </span>
          <span className="shell__folha-perfil">
            Perfil
            <i className="fas fa-chevron-right" aria-hidden />
          </span>
        </button>

        <div className="shell__folha-grade" role="list">
          {resto.map((m) => (
            <button
              key={m.id}
              type="button"
              role="listitem"
              className={`shell__folha-modulo${atualId === m.id ? ' shell__folha-modulo--on' : ''}`}
              aria-current={atualId === m.id ? 'page' : undefined}
              onClick={() => onIr(m.route)}
            >
              <span className="shell__folha-icone">
                <i className={m.icon} aria-hidden />
                {m.id === 'channels' ? <Selo n={naoLidasDoChat} rotulo={`${naoLidasDoChat} não lidas`} /> : null}
              </span>
              <span className="shell__folha-nome">{label(m)}</span>
            </button>
          ))}
          <button type="button" role="listitem" className="shell__folha-modulo" onClick={onMembros}>
            <span className="shell__folha-icone">
              <i className="fas fa-user-friends" aria-hidden />
            </span>
            <span className="shell__folha-nome">Membros</span>
          </button>
        </div>
      </div>
    </>
  )
}
